import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trophy, Crown, Award, CheckCircle, Gift, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Leaderboard = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voteBalance, setVoteBalance] = useState(0);

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`, {
        withCredentials: true
      });
      setLeaderboard(response.data);
      setLoading(false);
    } catch (e) {
      console.error('Failed to load leaderboard:', e);
      setLoading(false);
    }
  };

  const loadVoteBalance = async () => {
    try {
      const response = await axios.get(`${API}/vote-balance`, { withCredentials: true });
      setVoteBalance(response.data.vote_balance || 0);
    } catch (e) {
      console.error('Failed to load vote balance:', e);
    }
  };

  useEffect(() => {
    loadLeaderboard();
    loadVoteBalance();
  }, []);

  const getMedalIcon = (rank) => {
    switch (rank) {
      case 0:
        return <Crown className="w-8 h-8 text-yellow-500" />;
      case 1:
        return <Award className="w-7 h-7 text-gray-400" />;
      case 2:
        return <Award className="w-6 h-6 text-orange-600" />;
      default:
        return null;
    }
  };

  const getMedalBg = (rank) => {
    switch (rank) {
      case 0:
        return 'bg-gradient-to-br from-yellow-100 to-yellow-200 border-yellow-400';
      case 1:
        return 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-400';
      case 2:
        return 'bg-gradient-to-br from-orange-100 to-orange-200 border-orange-400';
      default:
        return 'bg-white border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              variant="ghost"
              size="icon"
              className="hover:bg-pink-100 rounded-full"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </Button>
            <div className="flex items-center space-x-2">
              <Trophy className="w-6 h-6 text-purple-600" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
                Dating Contest
              </h1>
            </div>
          </div>
          
          {/* Buy Votes Button */}
          <Button
            onClick={() => navigate('/buy-votes')}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
          >
            <Gift className="w-4 h-4 mr-2" />
            Buy Votes ({voteBalance})
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Info Banner */}
        <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl p-6 mb-8 text-white shadow-xl">
          <div className="flex items-center space-x-3 mb-3">
            <Trophy className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Vote for Your Favorite!</h2>
          </div>
          <p className="text-pink-50">
            Vote for someone to win an all expense paid date or an all expense paid vacation with their date! 
            The person with the most votes wins! 🎉
          </p>
        </div>

        {/* Leaderboard */}
        {leaderboard.length === 0 ? (
          <div className="text-center py-20">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No votes yet. Be the first to vote!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((item, index) => (
              <div
                key={item.user.id}
                data-testid={`leaderboard-rank-${index + 1}`}
                className={`${getMedalBg(index)} backdrop-blur-sm rounded-2xl p-4 border-2 shadow-lg hover:shadow-xl transform hover:-translate-y-1 cursor-pointer`}
                style={{transition: 'all 0.3s ease'}}
                onClick={() => navigate(`/profile/${item.user.id}`)}
              >
                <div className="flex items-center space-x-4">
                  {/* Rank */}
                  <div className="flex items-center justify-center w-12 h-12">
                    {getMedalIcon(index) || (
                      <span className="text-2xl font-bold text-gray-600">#{index + 1}</span>
                    )}
                  </div>

                  {/* Avatar */}
                  <Avatar className="w-16 h-16 border-2 border-white shadow-md">
                    <AvatarImage src={item.user.photos?.[0]?.startsWith('http') ? item.user.photos[0] : (item.user.photos?.[0] ? `${BACKEND_URL}${item.user.photos[0]}` : item.user.picture)} />
                    <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white text-xl">
                      {item.user.name[0]}
                    </AvatarFallback>
                  </Avatar>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-bold text-gray-800 truncate">
                        {item.user.name}
                      </h3>
                      {item.user.verification_status === 'approved' && (
                        <div className="flex items-center space-x-1 bg-blue-100 px-2 py-0.5 rounded-full flex-shrink-0">
                          <CheckCircle className="w-3 h-3 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-700">Verified</span>
                        </div>
                      )}
                      {index === 0 && (
                        <Badge className="bg-yellow-500 text-white text-xs">
                          Leading!
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {item.user.age} • {item.user.location}
                    </p>
                  </div>

                  {/* Vote Count */}
                  <div className="text-right">
                    <div className="flex items-center space-x-1 text-purple-700">
                      <Trophy className="w-5 h-5" />
                      <span className="text-2xl font-bold">{item.votes}</span>
                    </div>
                    <p className="text-xs text-gray-600">vote{item.votes !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg text-center">
          <p className="text-gray-700 mb-4">
            Browse profiles and vote for your favorite to help them win!
          </p>
          <Button
            data-testid="browse-profiles-btn"
            onClick={() => navigate('/discover')}
            className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full px-8 py-3"
          >
            Browse Profiles
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
